// src/pages/Company/AddCompany.jsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import companyService from '../../services/company.service';
import '../../styles/AddPages.css';

const AddCompany = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    companyName: '',
    companyCode: '',
    address: '',
    email: '',
    phone: '',
    gstin: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Auto-uppercase company code
    if (name === 'companyCode') {
      setFormData(prev => ({ ...prev, [name]: value.toUpperCase() }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    if (!formData.companyCode.trim()) {
      newErrors.companyCode = 'Company code is required';
    } else if (formData.companyCode.length < 3) {
      newErrors.companyCode = 'Company code must be at least 3 characters';
    } else if (!/^[A-Z0-9_-]+$/.test(formData.companyCode)) {
      newErrors.companyCode = 'Company code can only contain uppercase letters, numbers, underscore and hyphen';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (formData.phone && !/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = 'Phone must be 10 digits';
    }

    if (formData.gstin && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.gstin)) {
      newErrors.gstin = 'Invalid GSTIN format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.companyName.trim(),
        code: formData.companyCode.toUpperCase(),
        address: formData.address.trim(),
        email: formData.email.trim(),
        phone: formData.phone || undefined,
        gstin: formData.gstin || undefined,
      };

      const response = await companyService.createCompany(payload);

      if (response.success) {
        alert('Company added successfully!');
        navigate('/company/view');
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to add company');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-company-page">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a href="/dashboard/overview">Dashboard</a>
          </li>
          <li className="breadcrumb-item">
            <a href="/company/view">Companies</a>
          </li>
          <li className="breadcrumb-item active">Add Company</li>
        </ol>
      </nav>

      {/* Page Header */}
      <div className="page-header">
        <h4 className="page-title">Add New Company</h4>
      </div>

      {/* Form Card */}
      <div className="form-card">
        <form onSubmit={handleSubmit}>
          {/* Company Information */}
          <div className="form-section">
            
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  Company Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="companyName"
                  className={`form-input ${errors.companyName ? 'error' : ''}`}
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder="Enter company name"
                />
                {errors.companyName && (
                  <span className="error-message">{errors.companyName}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Company Code <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="companyCode"
                  className={`form-input ${errors.companyCode ? 'error' : ''}`}
                  value={formData.companyCode}
                  onChange={handleChange}
                  placeholder="Enter unique company code"
                  style={{ textTransform: 'uppercase' }}
                />
                {errors.companyCode && (
                  <span className="error-message">{errors.companyCode}</span>
                )}
                <small style={{ color: '#6b7280', fontSize: '12px' }}>
                  Uppercase letters, numbers, underscore and hyphen only
                </small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group form-group-full">
                <label className="form-label">
                  Address <span className="required">*</span>
                </label>
                <textarea
                  name="address"
                  className={`form-textarea ${errors.address ? 'error' : ''}`}
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Enter complete address"
                  rows="3"
                />
                {errors.address && (
                  <span className="error-message">{errors.address}</span>
                )}
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="form-section">            
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  className={`form-input ${errors.email ? 'error' : ''}`}
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="company@example.com"
                />
                {errors.email && (
                  <span className="error-message">{errors.email}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  className={`form-input ${errors.phone ? 'error' : ''}`}
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter 10-digit phone"
                  maxLength="10"
                />
                {errors.phone && (
                  <span className="error-message">{errors.phone}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">GSTIN</label>
                <input
                  type="text"
                  name="gstin"
                  className={`form-input ${errors.gstin ? 'error' : ''}`}
                  value={formData.gstin}
                  onChange={handleChange}
                  placeholder="Enter GSTIN"
                  maxLength="15"
                />
                {errors.gstin && (
                  <span className="error-message">{errors.gstin}</span>
                )}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/company/view')}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Adding Company...' : 'Add Company'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCompany;